import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { useIsOnline } from '@/features/offline/useSyncStatus';
import useAuthService from '@/hooks/use-auth-service';
import { ProfileUpdateError } from '@/services/AuthService';
import { PasswordFormValues, passwordSchema } from '../settings.schemas';
import { PasswordFields, usePasswordStrength } from './PasswordFields';

/**
 * Password change for the signed-in user. The strength requirement (zxcvbn
 * score of at least 2) is enforced here and again by the auth service.
 */
export function PasswordForm() {
  const authService = useAuthService();
  const isOnline = useIsOnline();

  const form = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { password: '', repeatPassword: '' },
  });

  const password = form.watch('password');
  const { strength, isStrongEnough, reset } = usePasswordStrength(password);

  const onSubmit = async (values: PasswordFormValues) => {
    if (!(await isStrongEnough(values.password))) {
      form.setError('password', {
        message: 'Please choose a stronger password',
      });
      return;
    }

    try {
      await authService.patchCurrentUser({ password: values.password });
      form.reset();
      reset();
      toast.success('Your password has been changed');
    } catch (error) {
      if (error instanceof ProfileUpdateError && error.status === 403) {
        form.setError('password', {
          message: 'Please choose a stronger password',
        });
        return;
      }
      toast.error('Something went wrong', {
        description:
          'Your password has not been changed. Please try again later.',
      });
    }
  };

  const { isSubmitting } = form.formState;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <PasswordFields form={form} strength={strength} />
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={!password || isSubmitting || !isOnline}
          >
            {isSubmitting && <Loader2 className="animate-spin" />}
            Change password
          </Button>
        </div>
      </form>
    </Form>
  );
}
